import React, { useState, useEffect, useRef } from 'react';
import { patientApi } from '../services/api';
import { speechToText } from '../services/lemonFoxApi';
import { storeFormData, getStoredFormData } from '../utils/sessionStorage';
import './VoiceIntakeForms.css';

/**
 * VoiceIntakeForms component for collecting patient information through voice recording
 * with rotating questions using LemonFox Speech-to-Text API
 *
 * @param {Object} props - Component props
 * @param {Object} props.tokenData - Patient token data
 * @param {Function} props.onSuccess - Callback function to call when forms are submitted
 * @param {Object} props.checkInData - Data from previous steps
 * @returns {JSX.Element} The voice intake forms component
 */
const VoiceIntakeForms = ({ tokenData, onSuccess, checkInData }) => {
  // State for recording status
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTranscript, setShowTranscript] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [formId, setFormId] = useState('patient_voice_intake');
  const [submissionId, setSubmissionId] = useState(null);
  
  // State for form data
  const [formData, setFormData] = useState({
    reasonForVisit: '',
    symptoms: '',
    medicationsOrAllergies: ''
  });
  
  // State for transcripts
  const [transcripts, setTranscripts] = useState({
    reasonForVisit: '',
    symptoms: '',
    medicationsOrAllergies: ''
  });
  
  // State for detailed transcription results
  const [transcriptionDetails, setTranscriptionDetails] = useState({
    reasonForVisit: null,
    symptoms: null,
    medicationsOrAllergies: null
  });
  
  // References for audio recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  
  // Define the questions
  const questions = [
    {
      id: 'reasonForVisit',
      question: 'Why do you want to talk to the provider today?',
      icon: 'help-circle',
      hint: 'Please describe the main reason for your visit'
    },
    {
      id: 'symptoms',
      question: 'Tell us more about the main symptoms you are having?',
      icon: 'thermometer',
      hint: 'Include when they started and how severe they are'
    },
    {
      id: 'medicationsOrAllergies',
      question: 'Do you have any changes to medications or allergies since your last visit?',
      icon: 'clipboard',
      hint: 'Include any new medications or allergies'
    }
  ];
  
  // Feather icons initialization removed to reduce CPU usage
  
  // Fetch form data from server when component mounts
  useEffect(() => {
    const fetchFormData = async () => {
      if (!tokenData || !tokenData.patient_id) {
        setIsLoading(false);
        return;
      }
      
      try {
        // First check if we have stored form data in sessionStorage
        const storedFormData = getStoredFormData(formId, tokenData.patient_id);
        let hasLocalData = false;
        
        if (storedFormData) {
          setFormData(storedFormData);
          setTranscripts(storedFormData);
          console.log('Loaded saved form data from sessionStorage:', storedFormData);
          hasLocalData = true;
        }
        
        // Then try to get data from the server
        const response = await patientApi.getIntakeFormData(tokenData.patient_id, tokenData);
        
        if (response.data && response.data.success) {
          // If we have existing form data from server and no local data, use it
          if (response.data.form_values && !hasLocalData) {
            setFormData(response.data.form_values);
            setTranscripts(response.data.form_values);
            
            // Store in sessionStorage for future use
            storeFormData(formId, tokenData.patient_id, response.data.form_values);
          }
          
          // Store form_id and submission_id for later use
          if (response.data.form_id) {
            setFormId(response.data.form_id);
          }
          
          if (response.data.submission_id) {
            setSubmissionId(response.data.submission_id);
          }
        }
      } catch (error) {
        console.error('Error fetching form data:', error);
        // Don't show error to user, just use default empty form
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFormData();
  }, [tokenData, formId]);
  
  // Cleanup function for audio recording
  useEffect(() => {
    return () => {
      // Stop any active media streams when component unmounts
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);
  
  // Start recording audio
  const startRecording = async () => {
    try {
      setError('');
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Create media recorder with MP3 format if supported
      const mimeType = MediaRecorder.isTypeSupported('audio/mp3')
        ? 'audio/mp3'
        : 'audio/webm';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      
      console.log(`Recording using MIME type: ${mimeType}`);
      
      // Clear previous audio chunks
      audioChunksRef.current = [];
      
      // Set up event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        try {
          // Create audio blob from chunks with the same MIME type used for recording
          const mimeType = mediaRecorderRef.current.mimeType;
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          
          // Transcribe audio using LemonFox API
          await transcribeAudio(audioBlob);
        } catch (error) {
          console.error('Error processing audio:', error);
          setError('Error processing audio. Please try again.');
          setIsRecording(false);
          setIsTranscribing(false);
        }
      };
      
      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      
      if (error.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access to use voice recording.');
      } else {
        setError('Could not start recording. Please check your microphone and try again.');
      }
    }
  };
  
  // Stop recording audio
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsTranscribing(true);
      
      // Stop all tracks in the stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };
  
  // Transcribe audio using LemonFox API
  const transcribeAudio = async (audioBlob) => {
    try {
      // Call LemonFox API to transcribe audio, passing submission ID and question index
      const result = await speechToText(audioBlob, submissionId || 'temp', currentQuestionIndex);
      
      // Get current question ID
      const currentQuestionId = questions[currentQuestionIndex].id;
      
      // Update transcript with the transcribed text
      setTranscripts(prev => ({
        ...prev,
        [currentQuestionId]: result.text
      }));
      
      // Store detailed transcription result
      setTranscriptionDetails(prev => ({
        ...prev,
        [currentQuestionId]: result
      }));
      
      // Update form data
      setFormData(prev => ({
        ...prev,
        [currentQuestionId]: result.text
      }));
      
      // Show transcript
      setShowTranscript(true);
      
    } catch (error) {
      console.error('Error transcribing audio:', error);
      setError('Error transcribing audio. Please try again or type your response manually.');
    } finally {
      setIsTranscribing(false);
    }
  };
  
  // Toggle recording
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
    
    // Re-initialize Feather icons after state changes
    setTimeout(() => {
      if (typeof window.feather !== 'undefined') {
        window.feather.replace();
      }
    }, 0);
  };
  
  // Move to the next question
  const handleNext = () => {
    // Save current transcript to form data
    const currentQuestion = questions[currentQuestionIndex].id;
    const currentValue = transcripts[currentQuestion] || '';
    
    // Update both form data and transcripts to ensure consistency
    setFormData(prev => {
      const updated = {
        ...prev,
        [currentQuestion]: currentValue
      };
      
      // Save to sessionStorage after updating
      if (tokenData && tokenData.patient_id) {
        storeFormData(formId, tokenData.patient_id, updated);
      }
      
      return updated;
    });
    
    // Ensure transcripts are also updated
    setTranscripts(prev => ({
      ...prev,
      [currentQuestion]: currentValue
    }));
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      // Check if we have a transcript for the next question
      const nextQuestion = questions[currentQuestionIndex + 1].id;
      setShowTranscript(!!transcripts[nextQuestion]);
    } else {
      handleSubmit();
    }
  };
  
  // Move to the previous question
  const handlePrevious = () => {
    // Save current transcript to form data
    const currentQuestion = questions[currentQuestionIndex].id;
    const currentValue = transcripts[currentQuestion] || '';
    
    // Update both form data and transcripts to ensure consistency
    setFormData(prev => {
      const updated = {
        ...prev,
        [currentQuestion]: currentValue
      };
      
      // Save to sessionStorage after updating
      if (tokenData && tokenData.patient_id) {
        storeFormData(formId, tokenData.patient_id, updated);
      }
      
      return updated;
    });
    
    // Ensure transcripts are also updated
    setTranscripts(prev => ({
      ...prev,
      [currentQuestion]: currentValue
    }));
    
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      // Check if we have a transcript for the previous question
      const prevQuestion = questions[currentQuestionIndex - 1].id;
      setShowTranscript(!!transcripts[prevQuestion]);
    }
  };
  
  // Submit the form
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      // Ensure all transcripts are saved to form data
      const currentQuestionId = questions[currentQuestionIndex].id;
      const currentValue = transcripts[currentQuestionId] || '';
      
      // First update the current question's value in both states
      setFormData(prev => {
        const updated = {
          ...prev,
          [currentQuestionId]: currentValue
        };
        
        // Save to sessionStorage after updating
        if (tokenData && tokenData.patient_id) {
          storeFormData(formId, tokenData.patient_id, updated);
        }
        
        return updated;
      });
      
      setTranscripts(prev => ({
        ...prev,
        [currentQuestionId]: currentValue
      }));
      
      // Then create the final form data with all values
      const updatedFormData = {
        ...formData,
        [currentQuestionId]: currentValue
      };
      
      // Add transcription details if available
      const transcriptionData = {};
      Object.keys(transcriptionDetails).forEach(key => {
        if (transcriptionDetails[key]) {
          transcriptionData[`${key}_details`] = transcriptionDetails[key];
        }
      });
      
      // Combine form data with transcription details
      const finalFormData = {
        ...updatedFormData,
        transcription_details: transcriptionData
      };
      
      // Send form data to backend
      await patientApi.submitIntakeForms(
        tokenData.patient_id,
        finalFormData,
        formId,
        submissionId,
        tokenData
      );
      
      // Notify parent component of success
      onSuccess({
        ...finalFormData,
        form_id: formId,
        submission_id: submissionId,
        patient_id: tokenData.patient_id,
        submitted_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error submitting intake forms:', error);
      setError('Failed to submit forms. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Toggle transcript visibility
  const toggleTranscript = () => {
    // If we're showing the transcript and toggling to edit mode,
    // make sure we have the latest value in the transcripts state
    if (showTranscript) {
      const currentQuestionId = questions[currentQuestionIndex].id;
      const currentValue = transcripts[currentQuestionId] || '';
      
      // Update transcripts state to ensure it has the latest value
      setTranscripts(prev => ({
        ...prev,
        [currentQuestionId]: currentValue
      }));
    }
    
    // Toggle the visibility
    setShowTranscript(!showTranscript);
  };
  
  
  // Get current question
  const currentQuestion = questions[currentQuestionIndex];
  
  return (
    <div className="card">
      <div className="card-body">
        <h3 className="card-title mb-4">Voice Check-In</h3>
        
        {isLoading ? (
          <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading your information...</p>
          </div>
        ) : (
          <>
            <div className="question-progress mb-4">
              {questions.map((q, index) => (
                <div
                  key={index}
                  className={`question-progress-item ${
                    index === currentQuestionIndex ? 'active' : ''
                  } ${index < currentQuestionIndex ? 'completed' : ''}`}
                  title={q.question}
                >
                  {index < currentQuestionIndex ? (
                    <i className="fas fa-check feather-sm"></i>
                  ) : (
                    index + 1
                  )}
                </div>
              ))}
            </div>
            
            {error && (
              <div className="alert alert-danger mb-3">
                {error}
              </div>
            )}
            
            <div className="question-container text-center mb-4">
              <div className="question-icon mb-3">
                <i className={`fas fa-${currentQuestion.icon === 'user' ? 'user' :
                                        currentQuestion.icon === 'calendar' ? 'calendar-alt' :
                                        currentQuestion.icon === 'phone' ? 'phone-alt' :
                                        currentQuestion.icon === 'mail' ? 'envelope' :
                                        currentQuestion.icon === 'clipboard' ? 'clipboard' :
                                        currentQuestion.icon === 'activity' ? 'heartbeat' :
                                        currentQuestion.icon === 'alert-circle' ? 'exclamation-circle' :
                                        currentQuestion.icon === 'help-circle' ? 'question-circle' :
                                        'comment'} fs-1`}></i>
              </div>
              <h4 className="mb-3">{currentQuestion.question}</h4>
              <p className="text-muted">{currentQuestion.hint}</p>
            </div>
            
            <div className={`recording-container text-center mb-4 ${isRecording ? 'recording-active' : ''}`}>
              <button
                className={`mic-btn btn btn-lg ${isRecording ? 'recording' : 'btn-primary'} p-4 mb-3`}
                onClick={toggleRecording}
                disabled={isSubmitting || isTranscribing}
                style={{margin: 'auto'}}
              >
                {isTranscribing ? (
                  <i className="fas fa-clock fs-1 text-warning"></i>
                ) : isRecording ? (
                  <i className="fas fa-volume-up fs-1 text-success"></i>
                ) : (
                  <i className="fas fa-microphone fs-1"></i>
                )}
              </button>
              
              <div>
                {isTranscribing ? (
                  <p>Transcribing your response...</p>
                ) : isRecording ? (
                  <p>Recording... Click to stop</p>
                ) : (
                  <p>Click to start recording</p>
                )}
              </div>
              
              <div className="mt-3">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={toggleTranscript}
                  disabled={isTranscribing}
                >
                  {showTranscript ? (
                    <i className="fas fa-eye-slash me-2"></i>
                  ) : transcripts[currentQuestion.id] ? (
                    <i className="fas fa-eye me-2"></i>
                  ) : (
                    <i className="fas fa-edit me-2"></i>
                  )}
                  {showTranscript ? 'Hide Response' : transcripts[currentQuestion.id] ? 'View Response' : 'Type Response'}
                </button>
              </div>
            </div>
            
            {/* Show transcript or text input */}
            <div className="transcript-container mb-4">
              {showTranscript && transcripts[currentQuestion.id] ? (
                <div className="card">
                  <div className="card-body">
                    <h5 className="card-title">Your Response</h5>
                    <p className="card-text">{transcripts[currentQuestion.id]}</p>
                    
                    
                    {/* Add manual edit option */}
                    <div className="mt-3">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          // Ensure we keep the current transcript value when switching to edit mode
                          const currentQuestionId = questions[currentQuestionIndex].id;
                          const currentValue = transcripts[currentQuestionId] || '';
                          
                          // Update transcripts state to ensure it has the latest value
                          setTranscripts(prev => ({
                            ...prev,
                            [currentQuestionId]: currentValue
                          }));
                          
                          // Switch to edit mode
                          setShowTranscript(false);
                        }}
                      >
                        <i className="fas fa-pen me-2"></i>
                        Edit manually
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card">
                  <div className="card-body">
                    <h5 className="card-title">Type your response</h5>
                    <p className="text-muted small mb-3">
                      If voice recording isn&apos;t working, you can type your response here.
                    </p>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={transcripts[currentQuestion.id] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        
                        // Update transcripts state
                        setTranscripts(prev => ({
                          ...prev,
                          [currentQuestion.id]: value
                        }));
                        
                        // Also update formData to keep states in sync
                        setFormData(prev => ({
                          ...prev,
                          [currentQuestion.id]: value
                        }));
                        
                        // Ensure we stay in edit mode
                        setShowTranscript(false);
                      }}
                      placeholder={`Type your answer to: ${currentQuestion.question}`}
                    ></textarea>
                  </div>
                </div>
              )}
            </div>
            
            <div className="d-flex justify-content-between mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0 || isSubmitting || isTranscribing}
              >
                Previous
              </button>
              
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNext}
                disabled={isSubmitting || isTranscribing || (!transcripts[currentQuestion.id] && !showTranscript)}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : currentQuestionIndex < questions.length - 1 ? (
                  'Next'
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceIntakeForms;